import React, { useState, useEffect } from 'react';
import { FocusedLayout } from '../../components/layout/FocusedLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, DollarSign, Image as ImageIcon, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CreateCampaignPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  
  const [formData, setFormData] = useState({
    campaign_name: '',
    business_name: '',
    headline: '',
    description: '',
    cta_text: 'Learn More',
    destination_link: '',
    placement: 'offers',
    duration_days: 7,
    target_country: 'All Countries',
    target_audience: 'Everyone',
    image_url: ''
  });

  const [calculatedCost, setCalculatedCost] = useState(0);

  useEffect(() => {
    fetchPricing();
    fetchWalletBalance();
  }, []);

  useEffect(() => {
    if (pricing && formData.placement && formData.duration_days) {
      const dailyPrice = pricing.placements[formData.placement]?.price_per_day || 0;
      setCalculatedCost(dailyPrice * formData.duration_days);
    }
  }, [pricing, formData.placement, formData.duration_days]);

  const fetchPricing = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ads/user/campaigns/pricing`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setPricing(data.pricing);
      }
    } catch (error) {
      toast.error('Failed to load pricing');
    }
  };

  const fetchWalletBalance = async () => {
    try {
      const response = await fetch(`${API_URL}/api/user/wallets`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        const walletMTR = data.data?.find(w => w.currency === 'MTR');
        setWalletBalance(walletMTR?.balance || 0);
      }
    } catch (error) {
      console.error('Failed to fetch wallet balance');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.campaign_name || !formData.business_name || !formData.headline) {
      toast.error('Please fill all required fields');
      return;
    }

    if (calculatedCost > walletBalance) {
      toast.error(`Insufficient MTR tokens. Required: ${calculatedCost.toLocaleString()}, Available: ${walletBalance.toLocaleString()}`);
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/ads/user/campaigns/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create campaign');
      }

      toast.success('Campaign created successfully! Awaiting admin approval.');
      navigate('/discover/mini-apps/matrise-ads/campaigns');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!pricing) {
    return (
      <FocusedLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">Loading...</div>
        </div>
      </FocusedLayout>
    );
  }

  const selectedPlacement = pricing.placements[formData.placement];

  return (
    <FocusedLayout>
      <div className="space-y-6 p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Create Campaign</h1>
            <p className="text-muted-foreground">Launch your advertising campaign</p>
          </div>
        </div>

        {/* Wallet Balance Alert */}
        <Card className="bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-blue-600" />
              <span className="font-semibold">Your MTR Balance: {walletBalance.toLocaleString()} MTR</span>
            </div>
          </CardContent>
        </Card>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Campaign Details */}
          <Card>
            <CardHeader>
              <CardTitle>Campaign Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Campaign Name *</Label>
                <Input
                  value={formData.campaign_name}
                  onChange={(e) => setFormData({...formData, campaign_name: e.target.value})}
                  placeholder="e.g., Summer Sale 2024"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Business Name *</Label>
                <Input
                  value={formData.business_name}
                  onChange={(e) => setFormData({...formData, business_name: e.target.value})}
                  placeholder="Your business name"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Headline *</Label>
                <Input
                  value={formData.headline}
                  onChange={(e) => setFormData({...formData, headline: e.target.value})}
                  placeholder="Catchy headline for your ad"
                  maxLength={100}
                  required
                />
                <p className="text-xs text-muted-foreground">{formData.headline.length}/100 characters</p>
              </div>

              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Describe your offering"
                  rows={3}
                  maxLength={250}
                  required
                />
                <p className="text-xs text-muted-foreground">{formData.description.length}/250 characters</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Call To Action *</Label>
                  <select
                    value={formData.cta_text}
                    onChange={(e) => setFormData({...formData, cta_text: e.target.value})}
                    className="w-full border rounded-md p-2"
                  >
                    {pricing.cta_options.map(cta => (
                      <option key={cta} value={cta}>{cta}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label>Destination Link *</Label>
                  <Input
                    value={formData.destination_link}
                    onChange={(e) => setFormData({...formData, destination_link: e.target.value})}
                    placeholder="https://..."
                    type="url"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Image URL (Recommended)</Label>
                <div className="flex gap-2">
                  <Input
                    value={formData.image_url}
                    onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                    placeholder="https://example.com/image.jpg"
                  />
                  <Button type="button" variant="outline">
                    <ImageIcon className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Recommended: 1200×600px • HTTPS image URL preferred (saves storage costs)
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Placement & Duration */}
          <Card>
            <CardHeader>
              <CardTitle>Placement & Duration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Placement *</Label>
                <div className="grid grid-cols-1 gap-3">
                  {Object.entries(pricing.placements).map(([key, placement]) => (
                    <div
                      key={key}
                      className={`border rounded-lg p-4 cursor-pointer transition ${
                        formData.placement === key 
                          ? 'border-blue-600 bg-blue-50 dark:bg-blue-950/30' 
                          : 'hover:border-gray-400'
                      }`}
                      onClick={() => setFormData({...formData, placement: key})}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-semibold">{placement.name}</div>
                          <div className="text-sm text-muted-foreground">{placement.description}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-lg">{placement.price_per_day.toLocaleString()} MTR</div>
                          <div className="text-xs text-muted-foreground">per day</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Duration (Days) *</Label>
                <Input
                  type="number"
                  value={formData.duration_days}
                  onChange={(e) => setFormData({...formData, duration_days: parseInt(e.target.value) || 1})}
                  min="1"
                  max={pricing.max_duration_days}
                  required
                />
                <p className="text-xs text-muted-foreground">Maximum: {pricing.max_duration_days} days</p>
              </div>
            </CardContent>
          </Card>

          {/* Targeting */}
          <Card>
            <CardHeader>
              <CardTitle>Targeting</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Target Country</Label>
                  <select
                    value={formData.target_country}
                    onChange={(e) => setFormData({...formData, target_country: e.target.value})}
                    className="w-full border rounded-md p-2"
                  >
                    {pricing.target_countries.map(country => (
                      <option key={country} value={country}>{country}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label>Target Audience</Label>
                  <select
                    value={formData.target_audience}
                    onChange={(e) => setFormData({...formData, target_audience: e.target.value})}
                    className="w-full border rounded-md p-2"
                  >
                    {pricing.target_audiences.map(audience => (
                      <option key={audience} value={audience}>{audience}</option>
                    ))}
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cost Summary */}
          <Card className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950 dark:to-blue-950">
            <CardContent className="pt-6">
              <div className="space-y-3">
                <div className="flex justify-between text-lg">
                  <span>Daily Rate ({selectedPlacement?.name}):</span>
                  <span className="font-semibold">{selectedPlacement?.price_per_day.toLocaleString()} MTR</span>
                </div>
                <div className="flex justify-between text-lg">
                  <span>Duration:</span>
                  <span className="font-semibold">{formData.duration_days} days</span>
                </div>
                <div className="border-t pt-3 flex justify-between text-2xl font-bold">
                  <span>Total Cost:</span>
                  <span className="text-blue-600">{calculatedCost.toLocaleString()} MTR</span>
                </div>
                {calculatedCost > walletBalance && (
                  <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 dark:bg-red-950/30 p-3 rounded">
                    <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                    <span>Insufficient balance. You need {(calculatedCost - walletBalance).toLocaleString()} more MTR tokens.</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={() => navigate(-1)} className="flex-1">
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading || calculatedCost > walletBalance}
              className="flex-1"
            >
              {loading ? 'Processing...' : `Pay ${calculatedCost.toLocaleString()} MTR & Submit`}
            </Button>
          </div>
        </form>
      </div>
    </FocusedLayout>
  );
};
